import React, { Component, useState, useEffect } from "react";

import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import Divider from "@material-ui/core/Divider";
import Fab from "@material-ui/core/Fab";
import FormControl from "@material-ui/core/FormControl";
import IconButton from "@material-ui/core/IconButton";
import LinearProgress from "@material-ui/core/LinearProgress";
import MenuItem from "@material-ui/core/MenuItem";
import OutlinedInput from "@material-ui/core/OutlinedInput";
import Paper from "@material-ui/core/Paper";
import Select from "@material-ui/core/Select";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import Toolbar from "@material-ui/core/Toolbar";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";

import AddIcon from "@material-ui/icons/Add";
import RefreshIcon from "@material-ui/icons/Refresh";

import ContentWrapper from "../../containers/ContentWrapper";
import AddAccountDialog from "./AddAccountDialog";

import accountService from "../../services/accountsService";

import devConfig from "../../aws-config/aws-exports";
import prodConfig from "../../aws-config-prod/aws-exports";

let awsConfig = process.env.REACT_APP_ENV === "production" ? prodConfig : devConfig;

const UserStatusMemo = React.memo(function UserStatus({ status }) {
  switch (status) {
    case "UNCONFIRMED":
      return (
        <Tooltip title="User has been created but not confirmed.." placement="bottom">
          <span>Unconfirmed</span>
        </Tooltip>
      );
    case "CONFIRMED":
      return (
        <Tooltip title="User has been confirmed." placement="bottom">
          <span>Confirmed</span>
        </Tooltip>
      );
    case "ARCHIVED":
      return (
        <Tooltip title="User is no longer active." placement="bottom">
          <span>Archived</span>
        </Tooltip>
      );
    case "UNKNOWN":
      return (
        <Tooltip title="User status is not known." placement="bottom">
          <span>Unknown</span>
        </Tooltip>
      );
    case "COMPROMISED":
      return (
        <Tooltip title=" User is disabled due to a potential security threat." placement="bottom">
          <span>Compromised</span>
        </Tooltip>
      );
    case "RESET_REQUIRED":
      return (
        <Tooltip
          title="User is confirmed, but the user must request a code and reset his or her password before he or she can sign in."
          placement="bottom"
        >
          <span>Reset required</span>
        </Tooltip>
      );
    case "FORCE_CHANGE_PASSWORD":
      return (
        <Tooltip title="User must change password on first sign-in" placement="bottom">
          <span>Change Password</span>
        </Tooltip>
      );
    default:
      return null;
  }
});

export default function UserAccount({ type }) {
  const [open, setOpen] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [failed, setFailed] = useState(false);
  const [users, setUsers] = useState([]);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("email");

  useEffect(() => {
    console.log("mounting", type);
    getAccounts();
  }, []);

  const openDialog = () => {
    setOpen(true);
  };

  const closeDialog = fetch => {
    setOpen(false);
    if (fetch) getAccounts();
  };

  const getAccounts = async () => {
    try {
      if (!fetching) setFetching(true);
      let body = await accountService.getAccounts({
        params: {
          type
        }
      });
      body = body.map(el => {
        if (el.Attributes.hasAvatar) {
          let id = el.Attributes.sub;
          el.Attributes.avatar = {
            src: `https://${awsConfig.aws_user_files_s3_bucket}.s3.${awsConfig.aws_user_files_s3_bucket_region}.amazonaws.com/public/users/${id}/avatar/avatar.png`
          };
        }
        return el;
      });
      setUsers(body);
      setFetching(false);
      setFailed(false);
    } catch (err) {
      console.log(err.code);
      setFetching(false);
      setFailed(true);
    }
  };

  const getSorting = (ordr, ordrBy) => {
    return ordr === "desc" ? (a, b) => desc(a, b, ordrBy) : (a, b) => -desc(a, b, ordrBy);
  };

  const stableSort = (array, cmp) => {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = cmp(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map(el => el[0]);
  };
  const desc = (a, b, orderBy) => {
    if (b[orderBy].toUpperCase() < a[orderBy].toUpperCase()) {
      return -1;
    }
    if (b[orderBy].toUpperCase() > a[orderBy].toUpperCase()) {
      return 1;
    }
    return 0;
  };

  const createSortHandler = property => {
    handleRequestSort(property);
  };
  const handleRequestSort = property => {
    const isDesc = orderBy === property && order === "desc";
    setOrder(isDesc ? "asc" : "desc");
    setOrderBy(property);
  };

  let sortData = users.map(a => {
    a.Attributes.Enabled = a.Enabled;
    a.Attributes.UserStatus = a.UserStatus;
    if (a.Attributes.given_name == undefined || a.Attributes.family_name == undefined) {
      a.Attributes.given_name = "N";
      a.Attributes.family_name = "A";
    }
    return a.Attributes;
  });
  let queryData = stableSort(sortData, getSorting(order, orderBy));
  const accountList = queryData.map((item, index) => {
    return (
      <TableBody key={index}>
        <TableRow>
          <TableCell>
            <div style={{ display: "flex", alignItems: "center" }}>
              <div
                style={{
                  position: "relative",
                  display: "inline",
                  float: "left"
                }}
              >
                <Avatar
                  style={{
                    height: 36,
                    width: 36,
                    fontSize: "0.9rem",
                    fontWeight: "bold",
                    color: "rgb(39, 174, 96)",
                    backgroundColor: "rgb(39, 174, 96,0.1)"
                  }}
                  src={item.hasAvatar ? item.avatar.src : null}
                >
                  {item.given_name && item.family_name && item.given_name[0].toUpperCase() + item.family_name[0].toUpperCase()}
                </Avatar>
                <div
                  style={{
                    position: "absolute",
                    right: 0,
                    bottom: 0,
                    width: 13,
                    height: 13,
                    background: "rgb(39, 174, 96)",
                    border: "2px solid #fff",
                    borderRadius: 100
                  }}
                />
              </div>
              {/* <div
                  style={{ display: "inline", float: "left", marginLeft: 10 }}
                >
                  {item.given_name && item.family_name
                    ? `${item.given_name} ${
                        item.family_name
                      }`
                    : "TBD"}
                </div> */}
              {item.given_name == "N" && item.family_name == "A" ? (
                <Typography color="textSecondary" style={{ marginLeft: 10, textTransform: "capitalize" }}>
                  <i>{`${item.given_name}/${item.family_name}`}</i>
                </Typography>
              ) : (
                <Typography color="textPrimary" style={{ marginLeft: 10, textTransform: "capitalize" }}>
                  {`${item.given_name} ${item.family_name}`}
                </Typography>
              )}
            </div>
          </TableCell>
          <TableCell>{item.email}</TableCell>
          <TableCell>{item.Enabled ? "Enabled" : "Disabled"}</TableCell>
          <TableCell>
            <UserStatusMemo status={item.UserStatus} />
          </TableCell>
          <TableCell>{item.role}</TableCell>
        </TableRow>
      </TableBody>
    );
  });

  const headRows = [
    { id: "given_name", numeric: false, disablePadding: false, label: "Name" },
    { id: "email", numeric: false, disablePadding: false, label: "Email" },
    { id: "", numeric: false, disablePadding: false, label: "Status" },
    { id: "", numeric: false, disablePadding: false, label: "Account Status" },
    { id: "", numeric: false, disablePadding: false, label: "Role" }
  ];

  return (
    <>
      <Toolbar variant="dense" style={{ padding: "10px 5px 20px" }} disableGutters={true}>
        <Typography variant="body2" color="textSecondary">
          Total Records: <b>{users.length}</b>
        </Typography>
        <div style={{ flexGrow: 1 }} />
        <div>
          <Tooltip title="Refresh List">
            <IconButton onClick={getAccounts} aria-label="Refresh">
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Add Account">
            <Fab onClick={openDialog} size="small" color="primary" aria-label="Add" style={{ marginLeft: "5px", boxShadow: "none" }}>
              <AddIcon />
            </Fab>
          </Tooltip>
        </div>
      </Toolbar>
      <Paper>
        {fetching && <LinearProgress color="primary" style={{ height: 2 }} />}
        {failed ? (
          <div style={{ padding: "20px 0", textAlign: "center" }}>
            <Typography variant="h3" gutterBottom style={{ color: "rgba(0,0,0,0.5)" }}>
              Something went wrong...
            </Typography>
            <Button variant="outlined" color="primary" onClick={getAccounts}>
              Reload
            </Button>
          </div>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                {headRows.map((row, key) => (
                  <TableCell
                    key={key}
                    align={row.numeric ? "right" : "left"}
                    padding={row.disablePadding ? "none" : "default"}
                    sortDirection={orderBy === row.id ? order : false}
                    onClick={row.id == "" ? null : () => createSortHandler(row.id)}
                    style={row.id == "" ? null : { cursor: "pointer" }}
                  >
                    {orderBy === row.id ? (
                      <TableSortLabel active={orderBy === row.id} direction={order}>
                        {row.label}
                      </TableSortLabel>
                    ) : (
                      row.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            {accountList}
          </Table>
        )}
      </Paper>

      <AddAccountDialog open={open} handleClose={closeDialog} type={type} />
    </>
  );
}
